import { NavigationStyles } from '@generated/graphql';
import { Link } from '@ovComponents/2-component/orgLink/orgLink';
import { UserContext } from '@providers/userContextProvider';
import {
  Breadcrumb as BreadcrumbLink,
  Breadcrumbs,
  type SxProps,
} from 'hephaestus/web';
import { Home } from 'hephaestus/webIcons';
import { kebabCase } from 'lodash-es';
import { type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatches } from 'react-router-dom';

type BreadcrumbLabel = string | (() => ReactNode);

export type BreadcrumbProp = {
  overrideColor?: string;
  sx?: SxProps;
};

const breadcrumbLabel = (handle: unknown): BreadcrumbLabel | undefined => {
  if (!handle || typeof handle !== 'object' || !('breadcrumb' in handle)) {
    return undefined;
  }
  const { breadcrumb: label } = handle as { breadcrumb?: BreadcrumbLabel };
  if (typeof label === 'string' || typeof label === 'function') {
    return label;
  }
  return undefined;
};

export const Breadcrumb = ({ overrideColor, sx }: BreadcrumbProp) => {
  const { t } = useTranslation();
  const { userContext } = useContext(UserContext);
  const matches = useMatches();
  const items = matches.flatMap((match) => {
    const label = breadcrumbLabel(match.handle);
    if (!label) return [];
    const testId =
      typeof label === 'function'
        ? `${kebabCase(label.name)}-link`
        : `${kebabCase(label)}-breadcrumb-link`;
    return [
      {
        href: match.pathname,
        label: typeof label === 'function' ? label : t(label),
        testId,
      },
    ];
  });

  let homeHref: string | undefined;
  if (userContext.role?.navigationStyle === NavigationStyles.Simple) {
    homeHref = items.splice(0, 2)[1]?.href;
  }

  if (items.length === 0) return null;

  const colorSx = overrideColor ? { color: overrideColor } : undefined;

  return (
    <Breadcrumbs sx={sx ?? colorSx}>
      {homeHref && (
        <BreadcrumbLink
          component={Link}
          data-testid='home-breadcrumb-link'
          aria-label={t('shared:home')}
          sx={colorSx}
          {...{ to: homeHref }}
        >
          <Home sx={{ fontSize: 'inherit' }} />
        </BreadcrumbLink>
      )}
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const Label = item.label;
        return (
          <BreadcrumbLink
            key={`${item.href}-${item.testId}`}
            current={isLast}
            component={isLast ? undefined : Link}
            data-testid={item.testId}
            sx={colorSx}
            {...(isLast ? {} : { to: item.href })}
          >
            {typeof Label === 'function' ? <Label /> : Label}
          </BreadcrumbLink>
        );
      })}
    </Breadcrumbs>
  );
};
